import React from "react";
import { Tag, Tooltip } from "antd";
import { ClockCircleOutlined, WarningOutlined } from "@ant-design/icons";

/**
 * FreshnessBadge — 物化宽表新鲜度标记（纯展示组件）
 *
 * 依据 completion_projection_design.md §4：宽表行带 refreshed_at，
 * 前端显示"数据截至 HH:MM"，超阈值变灰提示"数据可能陈旧"。
 *
 * 纯展示：只吃 props（refreshed_at 由调用方/mock 提供），不发请求。
 *
 * @param {string|number|Date} refreshed_at  物化层最近成功刷新时间(ISO 字符串/时间戳/Date)
 * @param {number} staleHours                陈旧阈值(小时)，默认 26h（每日批跑 + 容差）
 * @param {boolean} showDate                 是否在 tooltip 显示完整日期，默认 true
 */
function FreshnessBadge({ refreshed_at, staleHours = 26, showDate = true }) {
  if (refreshed_at == null) {
    return (
      <Tooltip title="物化宽表尚无成功刷新记录">
        <Tag icon={<WarningOutlined />} color="default">
          数据未生成
        </Tag>
      </Tooltip>
    );
  }

  const ts = refreshed_at instanceof Date ? refreshed_at : new Date(refreshed_at);
  if (isNaN(ts.getTime())) {
    return (
      <Tag icon={<WarningOutlined />} color="default">
        刷新时间无效
      </Tag>
    );
  }

  const pad = (n) => String(n).padStart(2, "0");
  const hhmm = `${pad(ts.getHours())}:${pad(ts.getMinutes())}`;

  const ageHours = (Date.now() - ts.getTime()) / 36e5;
  const stale = ageHours > staleHours;

  const fullLabel = `${ts.getFullYear()}-${pad(ts.getMonth() + 1)}-${pad(
    ts.getDate()
  )} ${hhmm}`;

  const label = `数据截至 ${hhmm}`;
  const tip = stale
    ? `数据可能陈旧（已过去约 ${Math.floor(ageHours)} 小时，阈值 ${staleHours}h）${
        showDate ? `，最近刷新 ${fullLabel}` : ""
      }`
    : showDate
    ? `物化读层最近刷新 ${fullLabel}`
    : label;

  return (
    <Tooltip title={tip}>
      <Tag
        icon={stale ? <WarningOutlined /> : <ClockCircleOutlined />}
        color={stale ? "default" : "green"}
        style={stale ? { color: "#8c8c8c" } : undefined}
      >
        {stale ? `${label}（数据可能陈旧）` : label}
      </Tag>
    </Tooltip>
  );
}

export default FreshnessBadge;
